import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetcher.js";
import type {
  OperatorOutstandingItemDecisionRequest,
  OperatorOutstandingItemDecisionResponse,
} from "./types.js";

/**
 * Phase 544 wave 4 task 4.1 (card #1990, AC-5) — record an outstanding item as RECEIVED, or as
 * NOT REQUIRED with a reason.
 *
 * <p><b>The `usePostDecision` shape, deliberately.</b> Wait-for-server-then-refetch, NO optimistic
 * update (D-DEC-FLOW-01): on 2xx it invalidates `['job', jobId]` and `['job-events', jobId]`, which
 * is what makes the item's new status AND its paired `outstanding_item_closed` /
 * `outstanding_item_waived` event appear together within one polling round-trip. Optimism would be
 * wrong here for a stronger reason than on an approval: the engine may refuse this write by name —
 * the row may already be settled, or the declaration may not admit a staff "received" at all
 * (`outstanding_item_not_staff_settled`) — and a panel that had already crossed the item off would
 * be showing a record that never happened.</p>
 *
 * <p><b>Why the JOB query and not just the items.</b> Recording an item also moves
 * `outstanding.open_count` / `outstanding.clear`, which the panel reads off the ENGINE's summary
 * rather than recomputing, and may release an approval the declaration held on that item. Both ride
 * on the job detail, so invalidating it is what keeps the summary line, the item list and the
 * Approve button from disagreeing about one file.</p>
 *
 * <p><b>Refusals.</b> `fetchWithAuth` throws `ApiError`, whose `bodyText` carries the engine's
 * `{error_code, message}`. The panel renders that through `readServerRefusal` verbatim — the
 * engine's sentence names which of the two acts is available on this row, and a generic sentence of
 * the console's own would turn a solvable refusal into a mystery.</p>
 *
 * <p>D-DEC-FLOW-02: no `X-Idempotency-Key` header; the record's audit pairing is the engine's.</p>
 */
export function usePostOutstandingItemDecision(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: OperatorOutstandingItemDecisionRequest) =>
      fetchWithAuth<OperatorOutstandingItemDecisionResponse>(
        `/internal/v0/jobs/${jobId}/outstanding-items/decisions`,
        {
          method: "POST",
          body: JSON.stringify(req),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      qc.invalidateQueries({ queryKey: ["job-events", jobId] });
    },
  });
}
